import React, { useEffect, useState } from 'react';
import { ShieldAlert, ShieldCheck } from 'lucide-react';
import { useDateFormat } from '@/hooks/useDateFormat';
import { getDeviceAuditLogs } from '@/services/auditLogService';

interface AtPolicyAuditEntry {
  id: string;
  /** ISO 8601 — the API serialises the Firestore timestamp. */
  timestamp?: string | null;
  userId?: string | null;
  source?: string;
  /** 'manual' for user-triggered, 'schedule' for scheduler, 'mcp' etc. Populated on flow-origin entries. */
  triggerSource?: string | null;
  /** Schedule doc id on scheduled runs. */
  scheduleId?: string | null;
  primitiveId?: string | null;
  modemImei?: string | null;
  originalCommand?: string;
  policyAction?: 'pass' | 'block';
  severity?: 'blocked' | 'pass' | 'exempt';
  blockReason?: string | null;
  offendingSubcommand?: string | null;
  allowedByPrimitive?: boolean;
  response?: string | null;
  policyVersion?: string;
}

interface Props {
  deviceId: string;
}

export const AtPolicyAuditTab: React.FC<Props> = ({ deviceId }) => {
  const fmt = useDateFormat();
  const [entries, setEntries] = useState<AtPolicyAuditEntry[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Read through the API. The direct Firestore subscription this replaced had
    // never returned a row: `audit_logs` has no read rule, so every snapshot
    // failed permission-denied straight into the console and left an empty
    // table. Keeping it client-side would mean opening the whole collection to
    // every authenticated user — list rules can't be scoped per document.
    let cancelled = false;
    setLoading(true);
    getDeviceAuditLogs(deviceId, 'at_command_policy', 100)
      .then((rows) => {
        if (!cancelled) setEntries(rows as AtPolicyAuditEntry[]);
      })
      .catch((err) => {
        console.error('Failed to load AT-policy audit entries:', err);
        if (!cancelled) setEntries([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [deviceId]);

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="w-8 h-8 border-4 border-accent-blue border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (entries.length === 0) {
    return (
      <div className="text-center py-12">
        <ShieldCheck className="w-16 h-16 text-status-success/70 mx-auto mb-4" />
        <p className="text-muted-foreground text-lg">No policy decisions recorded for this device</p>
        <p className="text-muted-foreground/70 text-sm mt-2">
          AT commands that are blocked or actioned by the safety policy appear here.
        </p>
      </div>
    );
  }

  const blockedCount = entries.filter((e) => e.policyAction === 'block').length;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          Showing {entries.length} most recent decisions{blockedCount > 0 ? ` — ${blockedCount} blocked` : ''}.
          Read-only. Served by <code className="text-xs">GET /audit-logs/device/:deviceId?kind=at_command_policy</code>.
        </p>
      </div>

      <div className="overflow-x-auto rounded-lg border border-border">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 text-muted-foreground">
            <tr className="text-left">
              <th className="py-2 px-3 font-medium">Time</th>
              <th className="py-2 px-3 font-medium">Action</th>
              <th className="py-2 px-3 font-medium">Source</th>
              <th className="py-2 px-3 font-medium">User</th>
              <th className="py-2 px-3 font-medium">Modem</th>
              <th className="py-2 px-3 font-medium">Command</th>
              <th className="py-2 px-3 font-medium">Reason</th>
            </tr>
          </thead>
          <tbody>
            {entries.map((e) => {
              const ts = e.timestamp ? new Date(e.timestamp) : null;
              const tsLabel = ts && !Number.isNaN(ts.getTime())
                ? `${fmt.formatDate(ts)} ${fmt.formatTime(ts)}`
                : '—';
              const blocked = e.policyAction === 'block';
              return (
                <tr key={e.id} className="border-t border-border">
                  <td className="py-2 px-3 text-xs text-muted-foreground whitespace-nowrap">{tsLabel}</td>
                  <td className="py-2 px-3">
                    {blocked ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-status-error-bg text-status-error border border-status-error/30">
                        <ShieldAlert className="w-3 h-3" /> Blocked
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-status-success-bg text-status-success border border-status-success/30">
                        <ShieldCheck className="w-3 h-3" /> Pass
                      </span>
                    )}
                  </td>
                  <td className="py-2 px-3 text-xs text-foreground">
                    {e.source || '—'}
                    {e.source === 'flow' && e.triggerSource === 'schedule' && (
                      <div className="text-[10px] text-muted-foreground/70" title={`schedule: ${e.scheduleId}`}>
                        scheduled
                      </div>
                    )}
                  </td>
                  <td className="py-2 px-3 text-xs text-muted-foreground truncate max-w-[10rem]">
                    {e.userId || (e.source === 'gateway_poll' ? 'gateway' : '—')}
                  </td>
                  <td className="py-2 px-3 font-mono text-xs text-muted-foreground">
                    {e.modemImei || '—'}
                  </td>
                  <td className="py-2 px-3">
                    <code className="text-xs text-foreground">{e.originalCommand || '—'}</code>
                    {e.offendingSubcommand && (
                      <div className="text-xs text-status-error mt-1">
                        offending: <code>{e.offendingSubcommand}</code>
                      </div>
                    )}
                  </td>
                  <td className="py-2 px-3 text-xs text-muted-foreground max-w-md">
                    {e.blockReason || '—'}
                    {e.allowedByPrimitive && (
                      <div className="text-xs text-status-warning mt-1">(allowlisted via primitive)</div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};
